import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { InboxChangeLog } from "@/components/kb/InboxChangeLog";
import { PageEditor } from "@/components/kb/PageEditor";
import { KbProgressStrip } from "@/components/kb/KbProgressStrip";
import { KbWorkflowExplainer } from "@/components/kb/KbWorkflowExplainer";
import { WikiTree, filterWikisForScope } from "@/components/kb/WikiTree";
import { useHermes } from "@/components/hermes/HermesProvider";
import { AdminAlert } from "@/components/shell/AdminPage";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ApiError } from "@/lib/api";
import { useAccountWorkspace } from "@/lib/account-workspace";
import {
  HERMES_WRITE_ACCEPTED_EVENT,
  hermesWriteAcceptedPageId,
  isHermesNewPageAccepted,
  shouldRefreshKbWorkbenchTree,
  type HermesWriteAcceptedDetail,
  type KnowledgeEntryFieldMeta,
} from "@/lib/hermes-knowledge-entry";
import { createKbApi, type KbPage, type KbWiki, type KbWikiProgress } from "@/lib/kb-api";
import { KB_COPY } from "@/lib/kb-copy";
import { emptySeedPages } from "@/lib/kb-page-status";
import { kbPagePath, kbWorkbenchPath } from "@/lib/kb-routes";
import { useKbCanWrite } from "@/lib/kb-permissions";

function fieldsForSeedPage(wiki: KbWiki, page: KbPage): KnowledgeEntryFieldMeta[] {
  const seedDef = wiki.seed_schema?.pages?.find((item) => item.slug === page.slug);
  const properties = seedDef?.properties ?? {};
  return Object.entries(properties).map(([key, schema]) => ({
    key,
    label: schema.description ?? key,
  }));
}

function preferKnowledgeEntryPage(pages: KbPage[], scope: "enterprise" | "brand"): KbPage | null {
  const empty = emptySeedPages(pages);
  if (empty.length > 0) return empty[0] ?? null;

  const preferredSlug = scope === "enterprise" ? "company-profile" : "brand-profile";
  return (
    pages.find((page) => page.is_seed && page.slug === preferredSlug) ??
    pages.find((page) => page.is_seed) ??
    pages[0] ??
    null
  );
}

/** 从 `kb/*` splat 解析深链 wikiId / pageId（避免 index 与深链分属两条 Route 导致整页 remount）。 */
function parseKbDeepLink(splat: string | undefined): {
  wikiId: string | undefined;
  pageId: string | undefined;
} {
  const match = (splat ?? "").match(/^wikis\/(\d+)\/pages\/(\d+)\/?$/);
  if (!match) return { wikiId: undefined, pageId: undefined };
  return { wikiId: match[1], pageId: match[2] };
}

export default function KbWorkbenchPage() {
  const params = useParams();
  const { accountId = "", brandId } = params;
  const { wikiId: wikiIdParam, pageId: pageIdParam } = parseKbDeepLink(params["*"]);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { modules } = useAccountWorkspace();
  const { startKnowledgeEntrySession } = useHermes();
  const scope = brandId ? "brand" : "enterprise";
  const contentScope = { accountId, brandId: brandId ?? null };
  const workbenchHref = kbWorkbenchPath(contentScope);
  /** 深链 /inbox（知识旅程）：右侧展示变更履历，不离开工作台壳 */
  const inboxMode = /\/inbox\/?$/.test(pathname);
  const kb = useMemo(() => createKbApi(accountId), [accountId]);
  const canWrite = useKbCanWrite();
  const kbEnabled = modules.some((module) => module.key === "kb" && module.enabled);

  const [wikis, setWikis] = useState<KbWiki[]>([]);
  const [pages, setPages] = useState<KbPage[]>([]);
  const [selectedWikiId, setSelectedWikiId] = useState<number | null>(
    wikiIdParam ? Number(wikiIdParam) : null,
  );
  const [selectedPageId, setSelectedPageId] = useState<number | null>(
    pageIdParam ? Number(pageIdParam) : null,
  );
  const [selectedPage, setSelectedPage] = useState<KbPage | null>(null);
  const [isLoadingWikis, setIsLoadingWikis] = useState(true);
  const [isLoadingPages, setIsLoadingPages] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newBody, setNewBody] = useState("");
  const [crossStackRedirect, setCrossStackRedirect] = useState<string | null>(null);
  const [progress, setProgress] = useState<KbWikiProgress | null>(null);
  const [progressLoading, setProgressLoading] = useState(false);
  /** 深链参数用 ref 读取，避免进入 /kb/wikis/... 时重建 loadWikis 导致整页 loading */
  const wikiIdParamRef = useRef(wikiIdParam);
  const pageIdParamRef = useRef(pageIdParam);
  wikiIdParamRef.current = wikiIdParam;
  pageIdParamRef.current = pageIdParam;
  const loadedPagesWikiIdRef = useRef<number | null>(null);

  const scopedWikis = useMemo(
    () => filterWikisForScope(wikis, scope, brandId),
    [wikis, scope, brandId],
  );
  const selectedWiki = scopedWikis.find((wiki) => wiki.id === selectedWikiId) ?? null;
  const hasSelectedWiki =
    selectedWikiId != null && scopedWikis.some((wiki) => wiki.id === selectedWikiId);

  const loadWikis = useCallback(async () => {
    setIsLoadingWikis(true);
    setError(null);
    setCrossStackRedirect(null);
    try {
      const payload = await kb.listWikis();
      setWikis(payload.wikis);

      const scoped = filterWikisForScope(payload.wikis, scope, brandId);
      const deepWikiId = wikiIdParamRef.current;
      const deepPageId = pageIdParamRef.current;

      // Pre-launch: if account /kb deep-link points at a brand wiki, send user to BrandHub stack.
      if (scope === "enterprise" && deepWikiId) {
        const deep = payload.wikis.find((wiki) => wiki.id === Number(deepWikiId));
        if (deep?.kind === "brand" && deep.brand_id) {
          const target = deepPageId
            ? kbPagePath(
                { accountId, brandId: String(deep.brand_id) },
                deep.id,
                Number(deepPageId),
              )
            : kbWorkbenchPath({ accountId, brandId: String(deep.brand_id) });
          setCrossStackRedirect(target);
          return;
        }
      }

      setSelectedWikiId((current) => {
        if (deepWikiId) {
          const fromParam = Number(deepWikiId);
          if (scoped.some((wiki) => wiki.id === fromParam)) return fromParam;
        }
        if (current && scoped.some((wiki) => wiki.id === current)) return current;
        return scoped[0]?.id ?? null;
      });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "无法加载 Wiki");
    } finally {
      setIsLoadingWikis(false);
    }
  }, [kb, scope, brandId, accountId]);

  const loadPages = useCallback(async (wikiId: number) => {
    const showLoading = loadedPagesWikiIdRef.current !== wikiId;
    if (showLoading) setIsLoadingPages(true);
    try {
      const payload = await kb.listPages(wikiId);
      setPages(payload.pages);
      loadedPagesWikiIdRef.current = wikiId;
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "无法加载知识条目");
    } finally {
      if (showLoading) setIsLoadingPages(false);
    }
  }, [kb]);

  const loadProgress = useCallback(
    async (wikiId: number) => {
      setProgressLoading(true);
      try {
        const payload = await kb.getWikiProgress(wikiId);
        setProgress(payload.progress);
      } catch {
        // 进度条为辅助信息；失败时保留上一次结果，不打断工作台主流程
      } finally {
        setProgressLoading(false);
      }
    },
    [kb],
  );

  useEffect(() => {
    void loadWikis();
  }, [loadWikis]);

  useEffect(() => {
    if (wikiIdParam) {
      setSelectedWikiId(Number(wikiIdParam));
    }
  }, [wikiIdParam]);

  // 条目列表 / 进度只随 wiki 变化拉取；切 page 不 refetch，避免左树与顶栏闪动
  useEffect(() => {
    if (!selectedWikiId) {
      setPages([]);
      loadedPagesWikiIdRef.current = null;
      setProgress(null);
      return;
    }
    if (!hasSelectedWiki) return;
    void loadPages(selectedWikiId);
    void loadProgress(selectedWikiId);
  }, [selectedWikiId, hasSelectedWiki, loadPages, loadProgress]);

  // 深链 pageId 或 pages 就绪后同步右侧编辑器；不卸载左栏
  useEffect(() => {
    if (pageIdParam) {
      const found = pages.find((page) => page.id === Number(pageIdParam)) ?? null;
      setSelectedPageId(found?.id ?? null);
      setSelectedPage(found);
      return;
    }

    setSelectedPageId((currentId) => {
      const preserved = currentId ? pages.find((page) => page.id === currentId) : null;
      const next = preserved ?? pages.find((page) => page.is_seed) ?? pages[0] ?? null;
      setSelectedPage(next);
      return next?.id ?? null;
    });
  }, [pages, pageIdParam]);

  // Hermes 确认写入后：仅当前打开的 Wiki 与提案目标一致时刷新侧栏；新建页则选中
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<HermesWriteAcceptedDetail>).detail;
      if (!detail) return;

      const currentWikiId = selectedWikiId;
      if (!shouldRefreshKbWorkbenchTree(detail, currentWikiId)) return;
      if (!currentWikiId) return;

      const pageId = hermesWriteAcceptedPageId(detail);

      void (async () => {
        try {
          const payload = await kb.listPages(currentWikiId);
          setPages(payload.pages);
          loadedPagesWikiIdRef.current = currentWikiId;
          void loadProgress(currentWikiId);

          if (pageId && isHermesNewPageAccepted(detail)) {
            const found = payload.pages.find((page) => page.id === pageId) ?? null;
            if (found) {
              setSelectedPageId(found.id);
              setSelectedPage(found);
              navigate(
                kbPagePath(
                  { accountId, brandId: brandId ?? null },
                  currentWikiId,
                  found.id,
                ),
              );
            }
          }
        } catch {
          // 侧栏刷新失败不打断；PageEditor 仍可能已按当前页同步
        }
      })();
    };

    window.addEventListener(HERMES_WRITE_ACCEPTED_EVENT, handler);
    return () => window.removeEventListener(HERMES_WRITE_ACCEPTED_EVENT, handler);
  }, [selectedWikiId, kb, loadProgress, navigate, accountId, brandId]);

  const selectPage = (pageId: number) => {
    const page = pages.find((item) => item.id === pageId) ?? null;
    setSelectedPageId(pageId);
    setSelectedPage(page);
    if (selectedWikiId) {
      navigate(kbPagePath(contentScope, selectedWikiId, pageId));
    }
  };

  const refreshProgress = () => {
    if (selectedWikiId) void loadProgress(selectedWikiId);
  };

  const resetCreateForm = () => {
    setNewTitle("");
    setNewBody("");
  };

  const handleCreateOpenChange = (open: boolean) => {
    setCreateOpen(open);
    if (!open) resetCreateForm();
  };

  const handleCreatePage = async () => {
    const title = newTitle.trim();
    const body = newBody.trim();
    if (!selectedWikiId || !title || !body) return;
    try {
      // slug 由服务端生成随机唯一串（见 Kb::SlugGenerator）
      const result = await kb.createPage({
        wiki_id: selectedWikiId,
        title,
        body,
        type: "concept",
      });
      setPages((current) => [...current, result.page]);
      setCreateOpen(false);
      resetCreateForm();
      // selectPage 导航到新条目；编辑/预览模式由 sessionStorage 跨页保持
      selectPage(result.page.id);
      refreshProgress();
      toast.success(KB_COPY.createSuccess);
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "创建失败");
    }
  };

  const handleDeletePage = async (pageId: number) => {
    try {
      await kb.deletePage(pageId);
      setPages((current) => current.filter((page) => page.id !== pageId));
      if (selectedPageId === pageId) {
        setSelectedPageId(null);
        setSelectedPage(null);
        navigate(workbenchHref);
      }
      refreshProgress();
      toast.success(KB_COPY.deleteSuccess);
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "删除失败");
    }
  };

  const handleAiKnowledgeEntry = () => {
    if (!selectedWiki || !accountId) {
      toast.error("请先进入知识库工作台");
      return;
    }

    const target = preferKnowledgeEntryPage(pages, scope);
    if (!target) {
      toast.error("当前知识库暂无可用条目，请先新增自由条目");
      return;
    }

    startKnowledgeEntrySession({
      mode: "knowledge_entry",
      accountId,
      pageId: target.id,
      wikiId: selectedWiki.id,
      pageSlug: target.slug,
      brandId: brandId ?? null,
      structured: target.structured ?? {},
      fields: fieldsForSeedPage(selectedWiki, target),
    });

    if (target.id !== selectedPageId) {
      selectPage(target.id);
    }
  };

  if (crossStackRedirect) {
    return <Navigate to={crossStackRedirect} replace />;
  }

  if (isLoadingWikis) {
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <p className="text-sm text-muted-foreground">
          正在加载{scope === "enterprise" ? "企业" : "品牌"}知识库…
        </p>
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
      {!kbEnabled ? (
        <AdminAlert tone="info">
          当前账户未订阅知识库产品。
          <Link to={`/accounts/${accountId}/subscriptions`} className="ml-1 underline">
            前往订阅
          </Link>
        </AdminAlert>
      ) : null}

      {error ? <AdminAlert>{error}</AdminAlert> : null}

      <KbWorkflowExplainer scope={scope} accountId={accountId} brandId={brandId} />

      {inboxMode ? (
        /* 知识旅程开：替换进度条 + 工作台主网格；Explainer 仍留在顶部 */
        <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-border bg-card">
          <InboxChangeLog
            accountId={accountId}
            wikiId={selectedWikiId}
            scope={scope}
          />
        </div>
      ) : (
        <>
          <KbProgressStrip progress={progress} loading={progressLoading} />

          <div className="grid min-h-0 flex-1 grid-cols-[minmax(240px,300px)_1fr] overflow-hidden rounded-lg border border-border bg-background">
            <aside className="min-h-0 overflow-hidden border-r border-border bg-muted/20">
              <WikiTree
                accountId={accountId}
                brandId={brandId}
                scope={scope}
                pages={pages}
                seedSchema={selectedWiki?.seed_schema}
                selectedPageId={selectedPageId}
                canWrite={canWrite}
                pagesLoading={isLoadingPages}
                onSelectPage={selectPage}
                onCreateFreePage={() => setCreateOpen(true)}
                onAiKnowledgeEntry={handleAiKnowledgeEntry}
                onDeletePage={(pageId) => void handleDeletePage(pageId)}
              />
            </aside>

            <section className="min-h-0 min-w-0 overflow-hidden bg-card">
              <PageEditor
                accountId={accountId}
                wiki={selectedWiki}
                page={selectedPage}
                canWrite={canWrite}
                onSaved={(page) => {
                  setSelectedPage(page);
                  setPages((current) => current.map((item) => (item.id === page.id ? page : item)));
                  refreshProgress();
                }}
              />
            </section>
          </div>
        </>
      )}

      <Dialog open={createOpen} onOpenChange={handleCreateOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{KB_COPY.newFreeEntry}</DialogTitle>
            <DialogDescription>{KB_COPY.freeCreateDescription}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="new-page-title">{KB_COPY.freeTitleLabel}</Label>
              <Input
                id="new-page-title"
                value={newTitle}
                onChange={(event) => setNewTitle(event.target.value)}
                placeholder={KB_COPY.freeTitlePlaceholder}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-page-body">{KB_COPY.freeBodyLabel}</Label>
              <Textarea
                id="new-page-body"
                value={newBody}
                onChange={(event) => setNewBody(event.target.value)}
                placeholder={KB_COPY.freeBodyPlaceholder}
                rows={6}
                className="min-h-28 resize-y rounded-md"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleCreateOpenChange(false)}>
              取消
            </Button>
            <Button
              type="button"
              onClick={() => void handleCreatePage()}
              disabled={!canWrite || !newTitle.trim() || !newBody.trim()}
            >
              创建
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
